import { Formik, Form } from 'formik';
import { useState } from 'react';
import { FormControlLabel, IconButton, Switch, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Dialog from '../../../components/UI/Dialog/Dialog';
import Button from '../../../components/UI/Button/Button';
import useTranslations from '../../../hooks/useTranslations';
import {
  IShippingCompany,
  IShippingCompanyCredentials,
} from '../../../models/shippingCompany';
import { ShippingCompaniesActions } from '../../../store/entities/shippingCompanies/shippingCompanies.actions';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store/type';
import { shippingCompanyManagementValidation } from '../../../utils/formValidation/shippingCompanyManagement';

interface IShippingCompanyProps {
  companyData: IShippingCompany | null;
  onClose: () => void;
  onSaved: (credentials?: IShippingCompanyCredentials) => void;
  canAdd: boolean;
  canUpdate: boolean;
}

const ShippingCompany = ({
  companyData,
  onClose,
  onSaved,
  canAdd,
  canUpdate,
}: IShippingCompanyProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { rcTranslate } = useTranslations();
  const [openForAll, setOpenForAll] = useState(
    companyData?.allowedIps?.includes('*') ?? false
  );
  const edit = Boolean(companyData?.id);

  const saveShippingCompany = async (
    body: Omit<IShippingCompany, 'id' | 'applicationId'>
  ) => {
    if (edit && companyData?.id) {
      return dispatch(
        ShippingCompaniesActions.updateShippingCompany(companyData.id, body)
      );
    }

    return dispatch(ShippingCompaniesActions.addShippingCompany(body));
  };

  return (
    <Formik
      initialValues={{
        nameHE: companyData?.nameHE ?? '',
        nameEN: companyData?.nameEN ?? '',
        allowedIps: companyData?.allowedIps?.length
          ? companyData.allowedIps
          : [''],
        isActive: companyData?.isActive ?? true,
      }}
      validateOnMount
      validationSchema={shippingCompanyManagementValidation}
      onSubmit={async (values, { setSubmitting }) => {
        const body = {
          ...values,
          allowedIps: openForAll ? ['*'] : values.allowedIps.filter(Boolean),
        };
        const response = await saveShippingCompany(body);
        setSubmitting(false);
        if (response?.isSucceeded) {
          onSaved(
            edit
              ? undefined
              : {
                  applicationId: response.content.applicationId,
                  clientSecret: response.content.clientSecret,
                }
          );
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        setFieldValue,
        isSubmitting,
        isValid,
      }) => (
        <Dialog
          title={
            edit
              ? 'configuration.shippingCompanyConfiguration.detailsTitle'
              : 'configuration.shippingCompanyConfiguration.addShippingCompany'
          }
          onClose={onClose}
          showFooter={false}
          className="manageShippingCompanies__dialog"
          body={
            <Form className="manageShippingCompanies__form">
              {edit && (
                <div className="manageShippingCompanies__readonlyGrid">
                  <TextField
                    className="manageShippingCompanies__disabledField"
                    label={rcTranslate(
                      'configuration.shippingCompanyConfiguration.fieldNames.entityIdentifier'
                    )}
                    value={String(companyData?.id) ?? ''}
                    disabled
                  />
                  <TextField
                    className="manageShippingCompanies__disabledField"
                    label={rcTranslate(
                      'configuration.shippingCompanyConfiguration.fieldNames.applicationId'
                    )}
                    value={companyData?.applicationId ?? ''}
                    disabled
                  />
                </div>
              )}
              <TextField
                name="nameHE"
                label={rcTranslate(
                  'configuration.shippingCompanyConfiguration.fieldNames.nameHE'
                )}
                value={values.nameHE}
                onChange={handleChange}
                error={Boolean(touched.nameHE && errors.nameHE)}
                helperText={
                  touched.nameHE && errors.nameHE ? errors.nameHE : ' '
                }
              />
              <TextField
                name="nameEN"
                label={rcTranslate(
                  'configuration.shippingCompanyConfiguration.fieldNames.nameEN'
                )}
                value={values.nameEN}
                onChange={handleChange}
                error={Boolean(touched.nameEN && errors.nameEN)}
                helperText={
                  touched.nameEN && errors.nameEN ? errors.nameEN : ' '
                }
              />
              <div className="manageShippingCompanies__ipHeader">
                <FormControlLabel
                  control={
                    <Switch
                      checked={openForAll}
                      onChange={(event) => {
                        setOpenForAll(event.target.checked);
                        setFieldValue(
                          'allowedIps',
                          event.target.checked ? ['*'] : ['']
                        );
                      }}
                    />
                  }
                  label={rcTranslate(
                    'configuration.shippingCompanyConfiguration.openForAll'
                  )}
                />
                {!openForAll && (
                  <IconButton
                    aria-label={rcTranslate(
                      'configuration.shippingCompanyConfiguration.addIp'
                    )}
                    onClick={() =>
                      setFieldValue('allowedIps', [...values.allowedIps, ''])
                    }
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </div>
              {!openForAll &&
                values.allowedIps.map((ip, index) => (
                  <div className="manageShippingCompanies__ipRow" key={index}>
                    <TextField
                      name={`allowedIps.${index}`}
                      label={`${rcTranslate('configuration.shippingCompanyConfiguration.fieldNames.ip')} ${index + 1}`}
                      value={ip}
                      onChange={handleChange}
                      error={Boolean(
                        Array.isArray(touched.allowedIps) &&
                        touched.allowedIps[index] &&
                        Array.isArray(errors.allowedIps) &&
                        errors.allowedIps[index]
                      )}
                      helperText={
                        (Array.isArray(touched.allowedIps) &&
                          touched.allowedIps[index] &&
                          Array.isArray(errors.allowedIps) &&
                          errors.allowedIps[index]) ||
                        ' '
                      }
                    />
                    <IconButton
                      aria-label={rcTranslate(
                        'configuration.shippingCompanyConfiguration.removeIp'
                      )}
                      onClick={() =>
                        setFieldValue(
                          'allowedIps',
                          values.allowedIps.filter(
                            (_, itemIndex) => itemIndex !== index
                          )
                        )
                      }
                      disabled={values.allowedIps.length === 1}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </div>
                ))}
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={values.isActive}
                    onChange={handleChange}
                  />
                }
                label={rcTranslate(
                  'configuration.shippingCompanyConfiguration.fieldNames.isActive'
                )}
              />
              <div className="manageShippingCompanies__dialogActions">
                {(edit ? canUpdate : canAdd) && (
                  <Button
                    title="button.save"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  />
                )}
                <Button
                  title="dialog.closeTitle"
                  onClick={onClose}
                  outlined
                  isCancelButton
                />
              </div>
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default ShippingCompany;
